/* eslint-disable no-undef */
/* eslint-disable import/prefer-default-export */
/* eslint-disable no-prototype-builtins */
import dayjs, { Dayjs, isDayjs } from 'dayjs';
import { getConfig } from '../APIConfig';
import AuthorizationError from '../AuthorizationError';
import DbVersionCheckError from '../DbVersionCheckError';
import { recursivelyChangeDateFormat, SERVER_DATE_FORMAT, toAPITDateTime } from '../helpers';
import FilterHelper from '../helpers/FilterHelper';
import LicenseError from '../LicenseError';
import SQLError from '../SQLError';
import TimesheetError from '../timeSheets/TimesheetError';
import ValidationError from '../ValidationError';
import isArrayLike from '../helpers/arrayLikeHelpers';

function formatQueryString<T = {}>(queryParams: { [key: string]: string | boolean | Dayjs | number | ArrayLike<T> | FilterHelper | undefined }) {
  const props = Object.entries(queryParams);
  const pieces: string[] = [];

  props.forEach((details) => {
    let val = details[1];
    if (val !== null && val !== undefined) {
      if (details[1] instanceof FilterHelper) {
        val = details[1].getResult();
      } else if (isArrayLike(val)) {
        val = Array.from(val as ArrayLike<any>).toString();
      } else if (isDayjs(val)) {
        val = toAPITDateTime(val);
      } else if (val instanceof Date) {
        val = toAPITDateTime(dayjs(val));
      } else if (isArrayLike(val)) {
        val = Array.from(val as ArrayLike<any>).toString();
      }
      pieces.push(`${details[0]}=${val}`);
    }
  });

  const output = pieces.join('&');
  if (output.length === 0) return '';

  return `?${output}`;
}

async function handleValidationResponse(resp: Response) {
  if (resp.status === 400) {
    const errorJson = await resp.json();
    throw new ValidationError(errorJson);
  }
}

async function handleUnauthorizedResponse(resp: Response) {
  if (resp.status === 401) {
    const errorJson = await resp.json();
    if (errorJson.value === 'LICENSE_ERROR') {
      throw new LicenseError();
    } else if (errorJson.value === 'TIMESHEET_ERROR') {
      const {
        message,
        status,
        comments,
        date,
      } : { message: string, status: string, comments: string, date: Dayjs } = errorJson;
      throw new TimesheetError(message, status, comments, date);
    } else {
      const { reason } = errorJson;
      throw new AuthorizationError(reason);
    }
  }
}

async function handle500Response(resp: Response) {
  if (resp.status === 500) {
    const errorJson = await resp.json();
    if (errorJson.value === 'SQL_ERROR') { throw new SQLError(errorJson.message); }
    if (Object.prototype.hasOwnProperty.call(errorJson, 'result')
        && Object.prototype.hasOwnProperty.call(errorJson, 'code')
        && Object.prototype.hasOwnProperty.call(errorJson, 'message')) {
      throw new DbVersionCheckError(errorJson);
    }
  }
}

type RequestType = 'GET' | 'PUT' | 'POST' | 'DELETE';

interface ISupportsQueryParams<RT> {
  // eslint-disable-next-line no-unused-vars
  AddQueryParams: (queryString?: { [key: string]: string | boolean | Date | number | ArrayLike<any> | FilterHelper}) => RT;
}

interface ISupportsBody<RT> {
  // eslint-disable-next-line no-unused-vars
  AddBody: <BT>(body: BT, dataType: 'json' | 'raw') => RT;
}

interface ISupportHeaders<RT> {
  // eslint-disable-next-line no-unused-vars
  AddHeaders: (headers: HeadersInit) => RT;
}

interface ISupportExecute {
  Execute: <T>() => Promise<T>;
}

type GET_TYPES = ISupportsQueryParams<GET_TYPES> & ISupportHeaders<GET_TYPES> & ISupportExecute;
export const Get = (url: string) => (
  new FetchContext<GET_TYPES>(url, 'GET')
);

type POST_TYPES = ISupportsQueryParams<POST_TYPES> & ISupportsBody<POST_TYPES> & ISupportHeaders<POST_TYPES> & ISupportExecute;
export const Post = (url: string) => (
  new FetchContext<POST_TYPES>(url, 'POST')
);

type PUT_TYPES = ISupportsQueryParams<PUT_TYPES> & ISupportsBody<PUT_TYPES> & ISupportHeaders<PUT_TYPES> & ISupportExecute;
export const Put = (url: string) => (
  new FetchContext<PUT_TYPES>(url, 'PUT')
);

type DELETE_TYPES = ISupportsQueryParams<DELETE_TYPES> & ISupportHeaders<DELETE_TYPES> & ISupportExecute;
export const Delete = (url: string) => (
  new FetchContext<DELETE_TYPES>(url, 'DELETE')
);

class FetchContext<T = (GET_TYPES | DELETE_TYPES | PUT_TYPES | POST_TYPES)> {
  url = '';

  body: any = undefined;

  requestType: RequestType = 'GET';

  query: { [key: string]: string | number | ArrayLike<any> | boolean | Dayjs | FilterHelper | undefined; } | undefined = {};

  dataType: 'json' | 'raw' = 'json';

  headers: HeadersInit = {};

  constructor(url: string, method: RequestType) {
    if (!url.startsWith('/')) throw new Error('url must start with /');

    this.url = url;
    this.requestType = method;
  }

  AddBody<BT>(body: BT, dataType: 'json' | 'raw' = 'json') {
    this.body = body;
    this.dataType = dataType;
    return this as unknown as T;
  }

  // eslint-disable-next-line class-methods-use-this
  AddQueryParams(queryString?: { [key: string]: string | number | ArrayLike<any> | boolean | Dayjs | FilterHelper | undefined; } | undefined) {
    this.query = queryString;
    return this as unknown as T;
  }

  AddHeaders(headers: HeadersInit) {
    this.headers = headers;
  }

  async Execute<RT = void>() : Promise<RT extends void ? void : RT> {
    const config = getConfig();

    let newPath = this.url;

    if (this.query) {
      newPath += formatQueryString(this.query);
    }

    const response = await fetch(`${config.baseUrl}${newPath}`, {
      method: this.requestType,
      body: this._formatBody(),
      headers: { ...this.headers, 'Content-Type': 'application/json; charset=utf-8' },
    });

    if (response.ok) {
      const text = await response.text();
      if (text.length === 0) {
        return {} as any;
      }

      const responseAsObj = JSON.parse(text);
      recursivelyChangeDateFormat(responseAsObj);
      return responseAsObj as any;
    }

    await handleUnauthorizedResponse(response);
    await handleValidationResponse(response);
    await handle500Response(response);

    throw new Error(`Http Request failed with ${response.status}.  ${response.statusText}`);
  }

  private _formatBody() {
    if (this.dataType === 'json') {
      return JSON.stringify(this.body, (k, v) => {
        if (isDayjs(v)) { return v.format(SERVER_DATE_FORMAT); }
        return v;
      });
    }

    return this.body;
  }
}
